///
/// EarthBrowser
///
/// BadgeGeometry - Flat rounded-rectangle fill or outline ring in the XY
/// plane, centred on the origin, two-sided (+Z and -Z faces) so it draws
/// from either side without culling concerns. A square with corner radius
/// = half the side is a circle. Used for the pressure-centre plate, ring
/// and pulse. Shares the "text_lit" vertex layout (position/normal/
/// texcoord) so it renders through TextRenderer alongside the marker text.
///
/// Created by Matt Giger on 10/23/2026
/// Copyright (c) 2026 Geodata Labs
///

import { type cube } from "@stovokor/math/cube"
import { type sphere } from "@stovokor/math/sphere"
import { type Geometry } from "@stovokor/shader/Geometry"
import { DrawMode } from "@stovokor/shader/GLConstants"
import { IndexBuffer } from "@stovokor/shader/IndexBuffer"
import { VertexAttribute } from "@stovokor/shader/VertexAttribute"
import { VertexBuffer } from "@stovokor/shader/VertexBuffer"
import { type StovokorContext } from "@stovokor/StovokorContext"

const cCornerSegments = 8

export interface BadgeShape {
	width: number
	height: number
	cornerRadius: number
	/** Outline stroke width; 0 (default) fills the whole shape */
	strokeWidth?: number
}

export class BadgeGeometry implements Geometry {
	public bounds: cube
	public boundingSphere: sphere
	public available: boolean = true

	public mode: DrawMode = DrawMode.Triangles
	public position: VertexAttribute
	public normal: VertexAttribute
	public texcoord: VertexAttribute
	public indexBuffer: IndexBuffer
	public vertexBuffer: VertexBuffer

	constructor(context: StovokorContext, shape: BadgeShape) {
		this.position = new VertexAttribute(context, 3)
		this.normal = new VertexAttribute(context, 3)
		this.texcoord = new VertexAttribute(context, 2)
		this.indexBuffer = new IndexBuffer(context)
		this.vertexBuffer = new VertexBuffer(context, [this.position, this.normal, this.texcoord])

		const outer = roundedRectOutline(shape.width, shape.height, shape.cornerRadius)
		const stroke = shape.strokeWidth ?? 0

		if (stroke > 0) {
			const inner = roundedRectOutline(
				Math.max(0, shape.width - 2 * stroke),
				Math.max(0, shape.height - 2 * stroke),
				Math.max(0, shape.cornerRadius - stroke)
			)
			this.addRing(outer, inner, 1)
			this.addRing(outer, inner, -1)
		} else {
			this.addFan(outer, 1)
			this.addFan(outer, -1)
		}

		this.bounds = this.position.bounds
		this.boundingSphere = this.bounds.sphere
	}

	private addFan(outline: { x: number, y: number }[], side: 1 | -1): void {
		const base = this.position.count
		this.position.addPos(0, 0, 0)
		this.normal.add(0, 0, side)
		this.texcoord.add(0.5, 0.5)
		for (const p of outline) {
			this.position.addPos(p.x, p.y, 0)
			this.normal.add(0, 0, side)
			this.texcoord.add(0.5 + p.x, 0.5 + p.y)
		}
		const n = outline.length
		for (let i = 0; i < n; i++) {
			const a = base + 1 + i
			const b = base + 1 + (i + 1) % n
			if (side > 0) this.indexBuffer.triangle(base, a, b)
			else this.indexBuffer.triangle(base, b, a)
		}
	}

	private addRing(outer: { x: number, y: number }[], inner: { x: number, y: number }[], side: 1 | -1): void {
		// Both outlines share the same vertex count and ordering
		const base = this.position.count
		const n = outer.length
		for (let i = 0; i < n; i++) {
			this.position.addPos(outer[i].x, outer[i].y, 0)
			this.normal.add(0, 0, side)
			this.texcoord.add(0, 0)
			this.position.addPos(inner[i].x, inner[i].y, 0)
			this.normal.add(0, 0, side)
			this.texcoord.add(1, 0)
		}
		for (let i = 0; i < n; i++) {
			const o0 = base + 2 * i
			const i0 = o0 + 1
			const o1 = base + 2 * ((i + 1) % n)
			const i1 = o1 + 1
			if (side > 0) {
				this.indexBuffer.triangle(o0, o1, i0)
				this.indexBuffer.triangle(i0, o1, i1)
			} else {
				this.indexBuffer.triangle(o0, i0, o1)
				this.indexBuffer.triangle(i0, i1, o1)
			}
		}
	}

	public destroy(): void {
		this.vertexBuffer.destroy()
		this.indexBuffer.destroy()
	}
}

/** CCW outline of a rounded rectangle centred on the origin */
function roundedRectOutline(width: number, height: number, radius: number): { x: number, y: number }[] {
	const hw = width / 2
	const hh = height / 2
	const r = Math.min(radius, hw, hh)
	const pts: { x: number, y: number }[] = []
	// Corner centres in CCW order starting bottom-right
	const corners = [
		{ cx: hw - r, cy: -hh + r, a0: -Math.PI / 2 },
		{ cx: hw - r, cy: hh - r, a0: 0 },
		{ cx: -hw + r, cy: hh - r, a0: Math.PI / 2 },
		{ cx: -hw + r, cy: -hh + r, a0: Math.PI },
	]
	for (const c of corners) {
		for (let s = 0; s <= cCornerSegments; s++) {
			const a = c.a0 + (s / cCornerSegments) * (Math.PI / 2)
			pts.push({ x: c.cx + r * Math.cos(a), y: c.cy + r * Math.sin(a) })
		}
	}
	return pts
}
